import type { ReactNode } from "react";
import { useState } from "react";
import type { SurveyOverview as Overview, SurveyOverviewFloor } from "../api/types.js";
import { Async } from "../components/Async.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { href } from "../app/router.js";

/**
 * A survey's rooms, floor by floor.
 *
 * ONE FLOOR AT A TIME, CHOSEN BY A BUTTON THAT SAYS WHAT IS ON IT. A survey
 * merged from two walks (0069) holds a capture per floor, and the plan editor's
 * floor picker is a dropdown somebody could not find at all. Here the floors
 * are buttons, labelled with their room count, and the one you are reading is
 * pressed — so "which floor am I looking at" is answered without opening
 * anything.
 *
 * WHAT EACH ROOM OWES, ON THE ROOM. A room with outstanding answers says how
 * many and links to where they are answered; a room with none says so. The
 * alternative — a survey-wide count somewhere else — tells a surveyor there
 * are nine things to do and nothing about where.
 *
 * A ROOM WITH NO FIGURE SAYS WHY. An unheated room is left out of the heat
 * load by the engine, and a dash with "not heated" beside it is the truth. A
 * zero would read as "this room needs no heat", which is a different claim and
 * a wrong one.
 */

export function SurveyRooms({ id }: { readonly id: string }): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<Overview>(() => client.surveyOverview(id), [client, id]);

  return (
    <Async state={state} what="this survey's rooms" reload={reload}>
      {(survey) => <Body survey={survey} />}
    </Async>
  );
}

function Body({ survey }: { readonly survey: Overview }): ReactNode {
  // The lowest floor first, which is where somebody starts walking.
  const [level, setLevel] = useState<number | null>(null);
  const floors = survey.floors;
  const shown =
    floors.find((f) => f.level === level) ?? floors[0] ?? null;

  return (
    <>
      <div className="page-header">
        <p className="small">
          <a href={href.survey(survey.surveyId)}>{survey.reference}</a>
        </p>
        <h1>Rooms</h1>
        <p className="lede">
          {survey.counts.rooms === 1 ? "One room" : `${survey.counts.rooms} rooms`} over{" "}
          {survey.counts.floors === 1 ? "one floor" : `${survey.counts.floors} floors`}.
          {survey.totalHeatLossW === null
            ? ` ${survey.noHeatLossReason ?? ""}`
            : ` The whole house needs ${Math.round(survey.totalHeatLossW)} W on the coldest day of the year.`}
        </p>
      </div>

      {shown === null ? (
        <div className="state state--empty">
          <p className="state__title">No rooms yet</p>
          <p className="state__body">
            Rooms appear here the moment they arrive from the phone. Nothing needs doing in this
            portal first.
          </p>
        </div>
      ) : (
        <>
          {floors.length > 1 ? (
            <div className="floor-picker" role="group" aria-label="Which floor">
              {floors.map((floor) => (
                <button
                  key={floor.level}
                  type="button"
                  className={`floor-picker__floor${
                    floor.level === shown.level ? " floor-picker__floor--on" : ""
                  }`}
                  aria-pressed={floor.level === shown.level}
                  onClick={() => setLevel(floor.level)}
                >
                  {floor.name}
                  <span className="floor-picker__count">
                    {floor.rooms.length === 1 ? "1 room" : `${floor.rooms.length} rooms`}
                  </span>
                </button>
              ))}
            </div>
          ) : null}

          <Floor floor={shown} surveyId={survey.surveyId} />
        </>
      )}
    </>
  );
}

function Floor({
  floor,
  surveyId,
}: {
  readonly floor: SurveyOverviewFloor;
  readonly surveyId: string;
}): ReactNode {
  return (
    <>
      <ul className="room-list" data-testid="room-list">
        {/* The WHOLE row opens the room, not just the amber corner: a room
            with nothing outstanding is still a room somebody wants to look at,
            and a row where only one corner is clickable teaches people to
            aim. */}
        {floor.rooms.map((room) => (
          <li key={room.roomId}>
            <a className="room-row" href={href.surveyRoom(surveyId, room.roomId)}>
              <span className="room-row__who">
              <span className="room-row__name">{room.name}</span>
              <span className="room-row__about">
                {room.floorAreaM2 === null ? "no area yet" : `${room.floorAreaM2.toFixed(1)} m²`}
                {room.unheated
                  ? ` · not heated${room.unheatedReason ? ` (${room.unheatedReason})` : ""}`
                  : room.designTempC === null
                    ? ""
                    : ` · warm to ${room.designTempC}°`}
              </span>
            </span>

            <span className="room-row__watts">
              {room.totalW === null ? "—" : `${Math.round(room.totalW)} W`}
            </span>

            {room.answersLeft > 0 ? (
              <span className="room-row__todo" data-testid="room-answers-left">
                {room.answersLeft === 1 ? "1 wall to answer" : `${room.answersLeft} walls to answer`}
              </span>
            ) : (
              <span className="room-row__done">✓ All answered</span>
            )}
            </a>
          </li>
        ))}
      </ul>

      <p className="floor-total">
        {floor.totalW === null
          ? "No figure for this floor yet."
          : `This floor needs ${Math.round(floor.totalW)} W.`}
      </p>
    </>
  );
}
